import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { Button, ButtonGroup, FormControlLabel, Input, Radio, RadioGroup } from '@mui/material'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { Consolidation } from '@returnmates/client-core/src/graphql/generated/api'
import {
  getConsolidations as getConsolidationsSelector,
  getContainersSelector,
  getPackingSlip as getPackingSlipSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { printBarcode } from '@returnmates/client-core/src/utils/printBarcode'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useField } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FLYOUT_SIDEBAR_TYPES } from '../../../constants/flyoutSidebar'
import UploadLabelField from '../../UploadLabelField'
import {
  MAX_CONTAINER_LABEL_COUNT,
  MIN_CONTAINER_LABEL_COUNT,
  PRINT_LABEL_SIZE_OPTIONS,
} from '../constants'
import useStyles from '../styles'
import { getContainerLabel, getRMLabel } from '../utils/labels'
import DownloadPDFLink from './DownloadPDFLink'

interface Props {
  id: string
  type: string
}

const LabelsAndManifest = ({ id, type }: Props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentPackingSlip = useSelector(getPackingSlipSelector)
  const consolidations = useSelector(getConsolidationsSelector)
  const containers = useSelector(getContainersSelector)
  const { input: labelUrlInput } = useField('labelUrl')
  const [isLabelUploaded, setIsLabelUploaded] = useState(false)
  const [labelCount, setLabelCount] = useState<number | string>(1)
  const [printLabelSize, setPrintLabelSize] = useState(PRINT_LABEL_SIZE_OPTIONS.dpi203)

  const currentConsolidation = useMemo(
    () => consolidations?.find(consolidation => consolidation.id === id),
    [consolidations, id],
  )

  const currentContainer = useMemo(() => containers?.find(container => container.id === id), [
    containers,
    id,
  ])

  const handleRemoveFile = useCallback(() => {
    setIsLabelUploaded(false)

    labelUrlInput.onChange(null)
  }, [labelUrlInput])

  const handlePrintLabelSizeUpdate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPrintLabelSize(event.target.value)
  }, [])

  const printRMLabel = useCallback(() => {
    printBarcode(
      getRMLabel(
        { consolidation: currentConsolidation || currentContainer || ({} as Consolidation) },
        printLabelSize,
      ),
    )
  }, [currentConsolidation, printLabelSize, currentContainer])

  const increaseLabelCount = useCallback(() => {
    setLabelCount((prev: number) => (prev < MAX_CONTAINER_LABEL_COUNT ? prev + 1 : prev))
  }, [])

  const decreaseLabelCount = useCallback(() => {
    setLabelCount((prev: number) => (prev > MIN_CONTAINER_LABEL_COUNT ? prev - 1 : prev))
  }, [])

  const printContainerLabel = useCallback(() => {
    printBarcode(
      getContainerLabel(
        { consolidation: currentConsolidation || currentContainer || ({} as Consolidation) },
        printLabelSize,
      ),
    )
  }, [currentConsolidation, printLabelSize, currentContainer])

  const handlePrintMultipleLabels = useCallback(() => {
    for (let i = 0; i < Number(labelCount); i++) {
      printContainerLabel()
    }
  }, [labelCount, printContainerLabel])

  const handleChangeLabelCount = useCallback(event => {
    const newValue = event.target.value
    if (newValue === '') {
      setLabelCount(newValue)
    } else {
      let numberValue = Number(newValue)
      numberValue = isNaN(numberValue)
        ? MIN_CONTAINER_LABEL_COUNT
        : Math.min(MAX_CONTAINER_LABEL_COUNT, Math.max(MIN_CONTAINER_LABEL_COUNT, numberValue))
      setLabelCount(numberValue)
    }
  }, [])

  const handleOnBlur = useCallback(() => {
    if (!labelCount) {
      setLabelCount(1)
    }
  }, [labelCount])

  const fetchPackingSlip = useCallback(async () => {
    if (currentPackingSlip?.[0]?.consolidationId !== id) {
      try {
        await createAsyncAction(dispatch, actions.getPackingSlipByConsolidationId.request({ id }))
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      }
    }
  }, [currentPackingSlip, dispatch, id])

  useEffect(() => {
    fetchPackingSlip()
  }, [fetchPackingSlip])

  return (
    <>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>{isLabelUploaded ? 'Label' : 'Upload label'}</p>
        <UploadLabelField
          labelUrl={labelUrlInput}
          setIsLabelUploaded={setIsLabelUploaded}
          dndStyles={classes.dndStyles}
          dropZoneLabel={
            <p className={classes.dropZoneLabel}>
              Drag & Drop or <span> Upload</span>
            </p>
          }
          onRemoveFile={handleRemoveFile}
        />
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Packing Slip</p>
        <DownloadPDFLink
          details={{
            ...(currentConsolidation || currentContainer || ({} as Consolidation)),
            currentPackingSlip,
          }}
        />
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Print Density</p>
        <RadioGroup row>
          <FormControlLabel
            checked={printLabelSize === PRINT_LABEL_SIZE_OPTIONS.dpi203}
            onChange={handlePrintLabelSizeUpdate}
            value={PRINT_LABEL_SIZE_OPTIONS.dpi203}
            control={<Radio />}
            label={PRINT_LABEL_SIZE_OPTIONS.dpi203}
          />
          <FormControlLabel
            checked={printLabelSize === PRINT_LABEL_SIZE_OPTIONS.dpi300}
            onChange={handlePrintLabelSizeUpdate}
            value={PRINT_LABEL_SIZE_OPTIONS.dpi300}
            control={<Radio />}
            label={PRINT_LABEL_SIZE_OPTIONS.dpi300}
          />
        </RadioGroup>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>RM Label</p>
        <p className={classes.PDFLink} onClick={printRMLabel}>
          Print RM Label
        </p>
      </div>

      {type === FLYOUT_SIDEBAR_TYPES.EDIT_CONTAINER ? (
        <>
          <div className={classes.detailsRow}>
            <p className={classes.inputHead}>Container Label</p>
            <ButtonGroup className={classes.counterWrap}>
              <Button
                onClick={decreaseLabelCount}
                disabled={labelCount === MIN_CONTAINER_LABEL_COUNT}
                className={classes.counterButton}
              >
                <RemoveIcon fontSize="small" />
              </Button>
              <Input
                type="number"
                size="small"
                className={classes.counterInput}
                onChange={handleChangeLabelCount}
                value={labelCount}
                classes={{ input: classes.counterInputRoot }}
                onBlur={handleOnBlur}
              />
              <Button
                onClick={increaseLabelCount}
                className={classes.counterButton}
                disabled={Number(labelCount) >= MAX_CONTAINER_LABEL_COUNT}
              >
                <AddIcon fontSize="small" />
              </Button>
            </ButtonGroup>
          </div>
          <p className={classes.PDFLink} onClick={handlePrintMultipleLabels}>
            Print Container Label
          </p>
        </>
      ) : null}
    </>
  )
}

export default memo(LabelsAndManifest)
